import { useEffect, useState } from 'react';
import { WORLD_PATHS } from '@/data/worldMapPaths';

// Convert geo coords to SVG coords
function geoToSvg(lon, lat) {
  const x = (lon + 180) / 360 * 1000;
  const y = (90 - lat) / 180 * 500;
  return [x, y];
}

// Key cities for markers and connections
const CITIES = [
  { lon: -77, lat: 38.9, label: 'Washington D.C.', primary: true },
  { lon: -74, lat: 40.7, label: 'New York' },
  { lon: -87.6, lat: 41.9, label: 'Chicago' },
  { lon: -118.2, lat: 34, label: 'Los Angeles' },
  { lon: -0.1, lat: 51.5, label: 'London' },
  { lon: 2.3, lat: 48.9, label: 'Paris' },
  { lon: 8.7, lat: 50.1, label: 'Frankfurt' },
  { lon: 55.3, lat: 25.3, label: 'Dubai' },
  { lon: 72.8, lat: 19, label: 'Mumbai' },
  { lon: 103.8, lat: 1.3, label: 'Singapore' },
  { lon: 139.7, lat: 35.7, label: 'Tokyo' },
  { lon: 116.4, lat: 39.9, label: 'Beijing' },
  { lon: 151.2, lat: -33.9, label: 'Sydney' },
  { lon: -46.6, lat: -23.5, label: 'São Paulo' },
  { lon: 3.4, lat: 6.5, label: 'Lagos' },
  { lon: 36.8, lat: -1.3, label: 'Nairobi' },
];

const WorldMap = ({ className = '' }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 400);
    return () => clearTimeout(timer);
  }, []);

  const cities = CITIES.map((c) => {
    const [x, y] = geoToSvg(c.lon, c.lat);
    return { ...c, x, y };
  });

  const dcCity = cities[0];
  const connectionTargets = [cities[4], cities[7], cities[10], cities[12], cities[9]];

  return (
    <div
      className={`${className}`}
      style={{
        opacity: visible ? 1 : 0,
        transition: 'opacity 1.2s cubic-bezier(0.16,1,0.3,1)',
      }}
    >
      <svg
        viewBox="0 0 1000 500"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full"
      >
        <defs>
          <radialGradient id="mapGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#3B82A0" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#3B82A0" stopOpacity="0" />
          </radialGradient>
          <filter id="dotGlow">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background glow */}
        <ellipse cx="500" cy="250" rx="490" ry="245" fill="url(#mapGlow)" />

        {/* Land masses from Natural Earth data */}
        {WORLD_PATHS.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="#3B82A0"
            fillOpacity="0.12"
            stroke="#3B82A0"
            strokeWidth="0.5"
            strokeOpacity="0.25"
          />
        ))}

        {/* Latitude grid lines */}
        {[100, 150, 200, 250, 300, 350, 400].map((y) => (
          <line
            key={`lat-${y}`}
            x1="20"
            y1={y}
            x2="980"
            y2={y}
            stroke="#3B82A0"
            strokeWidth="0.3"
            opacity="0.05"
            strokeDasharray="2,8"
          />
        ))}

        {/* Connection arcs from DC */}
        {connectionTargets.map((city, i) => {
          const midX = (dcCity.x + city.x) / 2;
          const midY = Math.min(dcCity.y, city.y) - 30 - i * 10;
          return (
            <path
              key={`conn-${i}`}
              d={`M${dcCity.x},${dcCity.y} Q${midX},${midY} ${city.x},${city.y}`}
              stroke="#3B82A0"
              strokeWidth="0.6"
              opacity="0.15"
              fill="none"
              strokeDasharray="3,5"
            />
          );
        })}

        {/* City markers */}
        {cities.map((city, i) => (
          <g key={`city-${i}`}>
            {/* Pulse ring */}
            <circle cx={city.x} cy={city.y} r={city.primary ? 6 : 3} fill="#3B82A0" opacity="0.06">
              <animate
                attributeName="r"
                values={city.primary ? '5;12;5' : '3;7;3'}
                dur={`${3 + i * 0.3}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0.1;0.02;0.1"
                dur={`${3 + i * 0.3}s`}
                repeatCount="indefinite"
              />
            </circle>
            {/* Dot */}
            <circle
              cx={city.x}
              cy={city.y}
              r={city.primary ? 3.5 : 1.8}
              fill="#3B82A0"
              opacity={city.primary ? 0.9 : 0.5}
              filter={city.primary ? 'url(#dotGlow)' : undefined}
            />
            {/* Bright center */}
            <circle
              cx={city.x}
              cy={city.y}
              r={city.primary ? 1.5 : 0.7}
              fill="white"
              opacity={city.primary ? 0.85 : 0.4}
            />
          </g>
        ))}
      </svg>
    </div>
  );
};

export default WorldMap;
